// src/components/subscription/PaymentMethodModal.jsx
// Modale selezione metodo di pagamento: Stripe / Wave / Orange Money / MTN
import React, { useState } from 'react';
import { XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

// Provider disponibili per regione
const PROVIDERS_BY_REGION = {
    EUROPE: [
        {
            id: 'STRIPE',
            label: 'Carta di credito / debito',
            icon: '💳',
            desc: 'Visa, Mastercard, American Express',
            needsPhone: false,
        },
    ],
    WEST_AFRICA: [
        {
            id: 'WAVE',
            label: 'Wave Mobile Money',
            icon: '🌊',
            desc: 'Guinea, Sénégal, Côte d\'Ivoire, Mali',
            needsPhone: false,
            color: '#1A9FDD',
        },
        {
            id: 'ORANGE_MONEY',
            label: 'Orange Money',
            icon: '🟠',
            desc: 'Sénégal, Côte d\'Ivoire, Guinée',
            needsPhone: true,
            phonePlaceholder: '+224 6XX XXX XXX',
            color: '#FF6600',
        },
        {
            id: 'MTN',
            label: 'MTN Mobile Money',
            icon: '🟡',
            desc: 'Guinée, Ghana, Cameroun',
            needsPhone: true,
            phonePlaceholder: '+224 6XX XXX XXX',
            color: '#FFC107',
        },
    ],
};

// Fallback: tutti i provider se regione sconosciuta
const ALL_PROVIDERS = [
    ...PROVIDERS_BY_REGION.EUROPE,
    ...PROVIDERS_BY_REGION.WEST_AFRICA,
];

const PaymentMethodModal = ({
    isOpen,
    onClose,
    onConfirm,
    loading,
    plan,
    billingCycle,
    amount,
    currency,
    region,
}) => {
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [phone, setPhone] = useState('');
    const [phoneError, setPhoneError] = useState('');

    if (!isOpen) return null;

    const providers = region === 'EUROPE'
        ? PROVIDERS_BY_REGION.EUROPE
        : region === 'WEST_AFRICA'
            ? PROVIDERS_BY_REGION.WEST_AFRICA
            : ALL_PROVIDERS;

    const selected = providers.find(p => p.id === selectedProvider);

    const currencyLabel = { EUR: '€', XOF: 'CFA', GNF: 'FG', GBP: '£' }[currency] || currency;
    const planLabel = { STARTER: 'Starter', PRO: 'Pro', ENTERPRISE: 'Enterprise' }[plan] || plan;
    const cycleLabel = billingCycle === 'yearly' ? 'anno' : 'mese';

    const validatePhone = (value) => {
        const digits = value.replace(/\D/g, '');
        if (digits.length < 8) return 'Inserisci un numero valido';
        return '';
    };

    const handleConfirm = () => {
        if (!selectedProvider) return;
        if (selected?.needsPhone) {
            const err = validatePhone(phone);
            if (err) { setPhoneError(err); return; }
        }
        onConfirm({ provider: selectedProvider, phone: selected?.needsPhone ? phone : undefined });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">

                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900">Scegli il metodo di pagamento</h2>
                        <p className="text-sm text-gray-500 mt-0.5">
                            Piano <strong>{planLabel}</strong> — {currencyLabel}{amount?.toLocaleString('it-IT')}/{cycleLabel}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
                        <XMarkIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Providers */}
                <div className="p-5 space-y-3">
                    {providers.map(provider => (
                        <button
                            key={provider.id}
                            onClick={() => { setSelectedProvider(provider.id); setPhone(''); setPhoneError(''); }}
                            className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition text-left ${
                                selectedProvider === provider.id
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-100 hover:border-gray-300 bg-white'
                            }`}
                        >
                            <span className="text-3xl">{provider.icon}</span>
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-900 text-sm">{provider.label}</p>
                                <p className="text-xs text-gray-400 truncate">{provider.desc}</p>
                            </div>
                            <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                                selectedProvider === provider.id
                                    ? 'border-blue-500 bg-blue-500'
                                    : 'border-gray-300'
                            }`}>
                                {selectedProvider === provider.id && (
                                    <div className="w-2.5 h-2.5 rounded-full bg-white" />
                                )}
                            </div>
                        </button>
                    ))}
                </div>

                {/* Campo telefono per Orange Money / MTN */}
                {selected?.needsPhone && (
                    <div className="px-5 pb-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Numero di telefono <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="tel"
                            value={phone}
                            onChange={e => { setPhone(e.target.value); setPhoneError(''); }}
                            placeholder={selected.phonePlaceholder || '+224 6XX XXX XXX'}
                            className={`w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                phoneError ? 'border-red-400' : 'border-gray-200'
                            }`}
                        />
                        {phoneError && <p className="text-red-500 text-xs mt-1">{phoneError}</p>}
                        <p className="text-xs text-gray-400 mt-1">
                            Riceverai una notifica push per confermare il pagamento
                        </p>
                    </div>
                )}

                {/* Footer */}
                <div className="p-5 pt-3 flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50 transition"
                    >
                        Annulla
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={!selectedProvider || loading}
                        className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {loading
                            ? <><ArrowPathIcon className="w-4 h-4 animate-spin" /> Caricamento...</>
                            : 'Conferma pagamento'
                        }
                    </button>
                </div>

                {/* Sicurezza */}
                <div className="px-5 pb-4 text-center text-xs text-gray-400">
                    🔒 Pagamento sicuro SSL · Cancella quando vuoi
                </div>
            </div>
        </div>
    );
};

export default PaymentMethodModal;
